import * as XLSX from 'xlsx'

/** Common Start */
export function isEmpty(value) {
  return value === undefined || value === null
}

export function isString(value) {
  return !isEmpty(value) && value !== ''
}

export function isArray(value) {
  return !isEmpty(value) && (getValueType(value) === 'array' && value.length > 0)
}

export function isObject(value) {
  return !isEmpty(value) && (getValueType(value) === 'object' && JSON.stringify(value) !== '{}')
}

export function formatDate(date, format) {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }
  if (isNaN(date.getTime())) {
    return '-'
  }
  format = format || 'yyyy-MM-dd HH:mm:ss'
  const padZero = (num) => (num < 10 ? `0${num}` : num)
  const tokens = {
    'y+': date.getFullYear(),          // 年
    'M+': date.getMonth() + 1,         // 月
    'd+': date.getDate(),              // 日
    'H+': date.getHours(),             // 24小时制时
    'h+': date.getHours() % 12 || 12,  // 12小时制时
    'm+': date.getMinutes(),           // 分
    's+': date.getSeconds(),           // 秒
    'a': date.getHours() < 12 ? 'am' : 'pm',
    'A': date.getHours() < 12 ? 'AM' : 'PM',
    'f+': date.getMilliseconds()       // 毫秒
  }
  return Object.entries(tokens).reduce((str, [token, value]) => {
    return str.replace(
      new RegExp(`(${token})`, 'g'),
      (match) => {
        // 处理年份的特殊情况 (yyyy -> 2023, yy -> 23)
        if (token === 'y+') {
          return value.toString().slice(-match.length)
        }
        // 处理毫秒 (fff -> 补零到3位)
        else if (token === 'f+') {
          return value.toString().padStart(3, '0').slice(0, match.length)
        }
        // 其他情况：匹配长度>1时补零
        return match.length === 1 ? value : padZero(value)
      }
    );
  }, format)
}
/**
 * 格式化日期时间字符串
 * @param {string|Date} dateStr - 日期时间字符串或 Date对象
 * @param {string} format - 格式化格式，默认 'yyyy-MM-dd HH:mm:ss'
 * @returns {string} - 格式化后的日期时间字符串
 */
export function formatDateTime(dateStr, format = 'yyyy-MM-dd HH:mm:ss') {
  // 空、null、undefined 直接返回-
  if (dateStr === null || dateStr === undefined || dateStr === '') {
    return '-'
  }
  let date;
  try {
    if (dateStr instanceof Date) {
      date = dateStr
    } else if (typeof dateStr === 'string') {
      date = new Date(dateStr.replace('T',' '))
    } else if (typeof dateStr === 'number') {
      date = dateStr.toString().length === 10 ? new Date(dateStr*1000) : new Date(dateStr)
    } else {
      return '-'
    }
    // 非法日期
    if (isNaN(date.getTime())) return '-'
  } catch(e) {
    return '-'
  }
  return formatDate(date, format)
}
/**
 * 计算剩余保修天数
 * @param {string|Date} activeTime - 激活时间字符串或 Date对象
 * @param {number} warrantyDays - 保修天数
 * @returns {number} - 剩余保修天数
 */
export function calculateRemainingWarranty(activeTime, warrantyDays) {
  
  if (!activeTime || !warrantyDays || warrantyDays <= 0) {
    return '-'
  }
  
  let activeDate
  
  if (activeTime instanceof Date) {
    activeDate = activeTime
  } else if (typeof activeTime === 'string') {
    if (activeTime.includes('T')) {
      activeDate = new Date(activeTime.replace('T', ' '))
    } else {
      activeDate = new Date(activeTime)
    }
  } else {
    return '-'
  }
  
  if (isNaN(activeDate.getTime())) {
    return '-'
  }
  
  
  const now = new Date()
  const timeDiff = now.getTime() - activeDate.getTime()
  const daysPassed = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
  const remainingDays = warrantyDays - daysPassed
  
  return remainingDays > 0 ? remainingDays : 0
}

export function downloadExportFile(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function exportToExcelByTableData(header, data, fileName) {
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.json_to_sheet(data, {
    header,
  })
  XLSX.utils.book_append_sheet(wb, ws, '第三方激活码信息')
  await XLSX.writeFile(wb, fileName)
}

export function regExpValidate(value, type = 'account') {
  let pass = false
  switch(type) {
    case 'digit': pass = /^\d+$/.test(value); break
    case 'email': pass = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value); break
    case 'sku': pass = /^[a-zA-Z0-9-]*$/.test(value); break
    default: pass = /^[a-zA-Z0-9]*$/.test(value); break
  }
  return pass
}
/** Common End */

/** LocalStorage Start */
export function getStorage(key) {
  const json = localStorage.getItem(key)
  return json2Value(json)
}

export function setStorage(key, value) {
  localStorage.setItem(key, value2Json(value))
}

export function rmStorage(key) {
  localStorage.removeItem(key)
}

export function clearStorage() {
  localStorage.clear()
}

function value2Json(value) {
  const obj = {
    type: getValueType(value),
    value: JSON.stringify(value),
  }
  return JSON.stringify(obj)
}

function json2Value(json) {
  try {
    let value = null
    const obj = JSON.parse(json)
    if (obj.type === undefined || obj.type === null || typeof obj.type !== 'string') {
      throw new Error()
    }
    if (obj.value === undefined || obj.value === null) {
      throw new Error()
    }
    switch(obj.type) {
      case 'string': value = obj.value; break
      case 'number': value = Number(obj.value); break
      case 'boolean': value = obj.value === 'true'; break
      case 'array': value = JSON.parse(obj.value); break
      case 'object': value = JSON.parse(obj.value); break
      default: break
    }
    return value
  } catch(e) {
    return null
  }
}
/** LocalStorage End */

function getValueType(value) {
  if (value === undefined) {
    return 'undefined'
  }
  if (value === null) {
    return 'null'
  }
  // 基本类型
  const baseType = typeof value
  if (baseType !== 'object') {
    return baseType
  }
  // 对象类型
  return Object.prototype.toString.call(value).slice(8, -1).toLowerCase()
}

export function getImageSize(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.readAsDataURL(file)
    fr.onload = function() {
      const image = new Image()
      image.src = fr.result
      image.onload = function() {
        const size = {
          width: 0,
          height: 0
        }
        size.width = image.width
        size.height = image.height
        resolve(size)
      }
      image.onerror = reject
    }
    fr.onerror = reject
  })
}